import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import ReactSelect from "react-select";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Card from "../../components/Card/Card";
import CardBody from "../../components/Card/CardBody";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import Button from "../../components/CustomButtons/Button";
import { client } from "../../services/axiosClient";
import {
  integrationColor,
  whiteColor,
  grayColor,
  blackColor,
} from "../../assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: "30px",
    maxWidth: "1400px",
    margin: "0 auto",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      padding: "15px",
    },
  },
  title: {
    marginBottom: "20px",
    textAlign: "center",
    color: grayColor[2],
    fontWeight: "300",
  },
  privacyNotice: {
    marginBottom: "15px",
    padding: "16px 20px",
    backgroundColor: "#e3f2fd",
    borderRadius: "8px",
    border: `1px solid #90caf9`,
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  privacyIcon: {
    color: "#1976d2",
    fontSize: "24px",
    marginTop: "2px",
  },
  privacyText: {
    color: "#0d47a1",
    fontSize: "14px",
    lineHeight: "1.6",
    margin: 0,
  },
  blockTitle: {
    marginBottom: "20px",
    marginLeft: "20px",
    color: integrationColor[0],
    fontWeight: "500",
  },
  questionCard: {
    marginBottom: "25px",
  },
  questionText: {
    marginBottom: "15px",
    fontWeight: "500",
    color: grayColor[2],
    fontSize: "15px",
  },
  commentField: {
    marginTop: "15px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: grayColor[5],
      },
      "&:hover fieldset": {
        borderColor: integrationColor[0],
      },
      "&.Mui-focused fieldset": {
        borderColor: integrationColor[0],
      },
      "& textarea": {
        resize: "vertical",
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: integrationColor[0],
    },
  },
  submitButton: {
    marginTop: "30px",
    marginBottom: "50px",
  },
  codeSection: {
    marginBottom: "30px",
    padding: "20px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: `1px solid ${grayColor[5]}`,
  },
  codeDisplay: {
    backgroundColor: "#f9f9f9",
    padding: "12px 16px",
    borderRadius: "4px",
    border: `1px solid ${grayColor[5]}`,
    fontFamily: "monospace",
    fontSize: "16px",
    fontWeight: "bold",
    color: integrationColor[0],
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "10px",
  },
  infoText: {
    color: grayColor[7],
    fontSize: "14px",
    marginTop: "8px",
    lineHeight: "1.5",
  },
  nameFields: {
    marginBottom: "30px",
  },
  requiredLabel: {
    color: "#f44336",
    marginLeft: "4px",
  },
  savingIndicator: {
    position: "fixed",
    top: "90px",
    right: "30px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    color: grayColor[7],
    fontSize: "13px",
    zIndex: 1100,
    transition: "all 0.3s ease",
    [theme.breakpoints.down("sm")]: {
      top: "70px",
      right: "15px",
      fontSize: "12px",
      padding: "8px 12px",
    },
  },
}));

const questionnaireData = [
  {
    blockId: 1,
    blockTitle: "Bloco 1: Perfil da Operação",
    questions: [
      {
        id: "q1_1",
        questionNumber: "1.1",
        text: "Qual o porte da sua operação de distribuição em termos de faturamento?",
        options: [
          {
            value: "a",
            label: "a) Faturamento anual até R$ 100 milhões",
          },
          {
            value: "b",
            label: "b) Faturamento anual entre R$ 100-300 milhões",
          },
          {
            value: "c",
            label: "c) Faturamento anual entre R$ 300-700 milhões",
          },
          {
            value: "d",
            label: "d) Faturamento anual entre R$ 700 milhões e R$ 1,5 bilhão",
          },
          {
            value: "e",
            label: "e) Faturamento anual acima de R$ 1,5 bilhão",
          },
        ],
      },
      {
        id: "q1_2",
        questionNumber: "1.2",
        text: "Qual o tamanho da sua base de clientes ativos?",
        options: [
          {
            value: "a",
            label: "a) Base de clientes até 500",
          },
          {
            value: "b",
            label: "b) Base de clientes entre 500-1500",
          },
          {
            value: "c",
            label: "c) Base de clientes entre 1500-3000",
          },
          {
            value: "d",
            label: "d) Base de clientes ativos entre 3000-5000",
          },
          {
            value: "e",
            label: "e) Base de clientes acima de 5000",
          },
        ],
      },
      {
        id: "q1_3",
        questionNumber: "1.3",
        text: "Como você segmenta e classifica sua base de clientes varejistas?",
        options: [
          {
            value: "a",
            label:
              "a) Não temos segmentação formal - tratamos todos os clientes de forma similar",
          },
          {
            value: "b",
            label:
              "b) Temos classificação básica por porte (pequeno/médio/grande) ou apenas por volume de compra",
          },
          {
            value: "c",
            label:
              "c) Segmentamos por canal/segmento de varejo (food service, farmácias, varejo alimentar, pet shops, etc.) mas sem critérios estruturados ou análise de comportamento",
          },
          {
            value: "d",
            label:
              "d) Temos segmentação estruturada considerando canal de varejo, porte, região e volume, com estratégias diferenciadas por segmento",
          },
          {
            value: "e",
            label:
              "e) Temos segmentação sofisticada com múltiplos critérios (canal de varejo, porte, localização, potencial, comportamento de compra, rentabilidade) e gestão comercial adaptada a cada segmento, com pelo menos 5-8 segmentos bem definidos",
          },
        ],
      },
    ],
  },
  {
    blockId: 2,
    blockTitle: "Bloco 2: Dados de Compras e Condições com Fornecedores",
    questions: [
      {
        id: "q2_1",
        questionNumber: "2.1",
        text: "Como você rastreia os custos de aquisição dos produtos que revende?",
        options: [
          {
            value: "a",
            label:
              "a) Temos apenas custos médios gerais por categoria de produto ou fornecedor",
          },
          {
            value: "b",
            label:
              "b) Temos custos de compra por produto, mas são médias simples sem considerar diferentes lotes ou condições de compra",
          },
          {
            value: "c",
            label:
              "c) Temos custos de compra por produto atualizados regularmente, mas sem distinguir diferentes condições comerciais e bonificações",
          },
          {
            value: "d",
            label:
              "d) Temos custos reais de aquisição por produto e fornecedor, incluindo diferentes preços de compra, mas bonificações e verbas são controladas separadamente",
          },
          {
            value: "e",
            label:
              "e) Temos rastreabilidade completa de custos de aquisição por produto, fornecedor e lote/data de compra, com integração de todas as condições comerciais (descontos, bonificações, verbas, prazos) para calcular custo líquido real",
          },
        ],
      },
      {
        id: "q2_2",
        questionNumber: "2.2",
        text: "Como você gerencia as condições comerciais e bonificações recebidas dos seus fornecedores?",
        options: [
          {
            value: "a",
            label:
              "a) Recebemos condições básicas de preço; bonificações e descontos não são sistematizados",
          },
          {
            value: "b",
            label:
              "b) Temos registro de algumas bonificações principais (por volume/crescimento), mas muitas condições comerciais ficam apenas em acordos não estruturados",
          },
          {
            value: "c",
            label:
              "c) Registramos as principais condições comerciais e bonificações de fornecedores, mas com dificuldade em vincular aos produtos vendidos ou alocar proporcionalmente",
          },
          {
            value: "d",
            label:
              "d) Temos bom controle de condições comerciais de fornecedores (verbas, bonificações por volume, prazos, investimentos cooperados) e conseguimos relacionar à maioria dos produtos",
          },
          {
            value: "e",
            label:
              "e) Temos gestão completa de todas as condições comerciais de fornecedores com rastreabilidade por produto e categoria, incluindo verbas de marketing cooperado, bonificações por desempenho, investimentos em trade marketing, logística reversa, e conseguimos calcular o custo real líquido de aquisição por SKU",
          },
        ],
      },
    ],
  },
  {
    blockId: 3,
    blockTitle: "Bloco 3: Dados de Vendas e Condições com Clientes",
    questions: [
      {
        id: "q3_1",
        questionNumber: "3.1",
        text: "Qual o nível de detalhe das suas informações de vendas?",
        options: [
          {
            value: "a",
            label:
              "a) Temos apenas informações agregadas de receita total por mês ou período",
          },
          {
            value: "b",
            label:
              "b) Temos informações de vendas por cliente ou por produto, mas não ambos simultaneamente",
          },
          {
            value: "c",
            label:
              "c) Temos informações detalhadas de vendas por cliente e produto, mas com limitações em outras dimensões (canal, região, equipe de vendas, etc.)",
          },
          {
            value: "d",
            label:
              "d) Temos informações completas de vendas no nível nota fiscal incluindo cliente, produto, volume, valor, data e algumas dimensões adicionais (canal, rota, vendedor)",
          },
          {
            value: "e",
            label:
              "e) Temos informações completas e históricas (12+ meses) no nível nota fiscal/pedido com múltiplas dimensões (cliente, produto, rota, região, vendedor, segmento de varejo, categoria de produto, etc.)",
          },
        ],
      },
      {
        id: "q3_2",
        questionNumber: "3.2",
        text: "Qual o nível de detalhe das suas informações de descontos e condições comerciais concedidas aos clientes?",
        options: [
          {
            value: "a",
            label:
              "a) Não temos visibilidade detalhada - descontos aparecem apenas como redução da receita bruta consolidada",
          },
          {
            value: "b",
            label:
              "b) Conseguimos identificar o valor total de descontos por tipo (comercial, volume, promocional), mas sem detalhar por cliente ou produto",
          },
          {
            value: "c",
            label:
              "c) Temos informações de descontos e prazos por cliente, mas com dificuldade em vincular a produtos ou transações específicas",
          },
          {
            value: "d",
            label:
              "d) Temos informações de descontos e condições comerciais vinculadas a clientes e produtos, cobrindo a maioria dos tipos de negociações",
          },
          {
            value: "e",
            label:
              "e) Temos rastreabilidade completa de todos os descontos, prazos de pagamento, bonificações, devoluções e condições especiais por transação, cliente, produto e segmento, incluindo descontos escalonados por volume",
          },
        ],
      },
    ],
  },
  {
    blockId: 4,
    blockTitle: "Bloco 4: Custos Operacionais e Alocação",
    questions: [
      {
        id: "q4_1",
        questionNumber: "4.1",
        text: "Como sua empresa aloca custos logísticos (transporte, armazenagem, separação, entrega)?",
        options: [
          {
            value: "a",
            label:
              "a) Não alocamos - tratamos como custo fixo geral da operação",
          },
          {
            value: "b",
            label:
              "b) Alocamos de forma simplificada (por exemplo, percentual fixo sobre vendas ou rateio linear)",
          },
          {
            value: "c",
            label:
              "c) Alocamos por região ou rota de entrega, mas sem vincular a clientes ou produtos específicos",
          },
          {
            value: "d",
            label:
              "d) Temos informações que permitem alocação por critérios relevantes (rotas, volumes, peso, cubagem, frequência de entrega, distância, CD de origem)",
          },
          {
            value: "e",
            label:
              "e) Temos rastreabilidade detalhada dos custos logísticos por cliente/entrega, incluindo diferentes tipos de veículo, rotas otimizadas, horários, custos de armazenagem e separação por produto (baseado em peso, volume, temperatura, fragilidade)",
          },
        ],
      },
      {
        id: "q4_2",
        questionNumber: "4.2",
        text: "Como sua empresa gerencia custos comerciais (equipes de vendas, crédito e cobrança, relacionamento)?",
        options: [
          {
            value: "a",
            label:
              "a) Tratamos como custos fixos gerais sem qualquer vinculação a clientes, produtos ou territórios",
          },
          {
            value: "b",
            label:
              "b) Conseguimos identificar custos totais por área (vendas, cobrança), mas sem detalhar alocação",
          },
          {
            value: "c",
            label:
              "c) Temos estrutura comercial organizada por território/região e conseguimos identificar custos por essas dimensões",
          },
          {
            value: "d",
            label:
              "d) Temos definido uma dedicação ideal de cada vendedor para cada cliente, e conseguimos vincular equipes comerciais, custos de crédito e inadimplência a segmentos de clientes ou territórios específicos",
          },
          {
            value: "e",
            label:
              "e) Temos controle detalhado de custos comerciais incluindo registro real de horas dedicadas dos vendedores por cliente, custo de crédito individualizado (prazo médio × custo de capital), inadimplência histórica por cliente/segmento e esforço de cobrança alocável",
          },
        ],
      },
      {
        id: "q4_3",
        questionNumber: "4.3",
        text: "Como você gerencia investimentos de trade marketing e execução nos pontos de venda?",
        options: [
          {
            value: "a",
            label:
              "a) Não realizamos atividades estruturadas de trade marketing ou execução em lojas",
          },
          {
            value: "b",
            label:
              "b) Realizamos ações pontuais de trade marketing, mas sem registro sistemático de investimentos por cliente ou produto",
          },
          {
            value: "c",
            label:
              "c) Temos equipe de trade marketing/merchandising, mas controle limitado de horas dedicadas, custos e efetividade das ações por cliente",
          },
          {
            value: "d",
            label:
              "d) Registramos investimentos de trade marketing por cliente e tipo de ação (materiais promocionais, horas de promotores, degustações), mas sem vincular a produtos ou resultados específicos",
          },
          {
            value: "e",
            label:
              "e) Temos controle completo de investimentos de trade marketing e execução por cliente, produto, tipo de ação e período, incluindo custos detalhados de equipe de merchandising, materiais, ações promocionais e conseguimos vincular investimentos a resultados de venda",
          },
        ],
      },
      {
        id: "q4_4",
        questionNumber: "4.4",
        text: "Como sua empresa controla custos administrativos (financeiro, RH, TI, jurídico, diretoria)?",
        options: [
          {
            value: "a",
            label:
              "a) Tratamos como custos fixos gerais sem controle detalhado por área",
          },
          {
            value: "b",
            label:
              "b) Temos controle básico do total de despesas administrativas por área funcional",
          },
          {
            value: "c",
            label:
              "c) Controlamos despesas por área e acompanhamos alguns indicadores de produtividade (ex: transações por funcionário, pedidos processados)",
          },
          {
            value: "d",
            label:
              "d) Temos controle estruturado de custos por área com indicadores de produtividade definidos (ex: custo por pedido processado, custo por nota fiscal emitida, tempo médio de processamento) e acompanhamento mensal do desempenho",
          },
          {
            value: "e",
            label:
              "e) Temos controle detalhado de custos administrativos com múltiplos indicadores de produtividade por área, metas de eficiência estabelecidas, análise de tendências e iniciativas de melhoria contínua baseadas nos indicadores",
          },
        ],
      },
    ],
  },
  {
    blockId: 5,
    blockTitle:
      "Bloco 5: Infraestrutura, Maturidade Analítica e Potencial de Valor",
    questions: [
      {
        id: "q5_1",
        questionNumber: "5.1",
        text: "Como estão organizados e integrados os dados da sua operação?",
        options: [
          {
            value: "a",
            label:
              "a) Dados dispersos em múltiplos sistemas sem integração, extrações manuais frequentes em planilhas",
          },
          {
            value: "b",
            label:
              "b) Temos sistema de gestão integrado (ERP), mas com pouca integração com outros sistemas e necessidade de conciliação manual constante",
          },
          {
            value: "c",
            label:
              "c) Temos sistema de gestão integrado (ERP) que registra vendas e compras de forma integrada, mas dados auxiliares (rotas, estruturas, trade marketing, condições comerciais) ainda em planilhas",
          },
          {
            value: "d",
            label:
              "d) Boa integração entre sistema de gestão e sistemas auxiliares (gerenciamento de armazém, roteirização, força de vendas) com processos estabelecidos de extração mensal e qualidade de dados razoável",
          },
          {
            value: "e",
            label:
              "e) Infraestrutura de dados madura com informações consolidadas de vendas, compras, estoque, logística, financeiro e trade marketing disponíveis de forma integrada, com processos automatizados de extração e validação",
          },
        ],
      },
      {
        id: "q5_2",
        questionNumber: "5.2",
        text: "Com que frequência você consegue atualizar e consolidar suas informações operacionais e financeiras?",
        options: [
          {
            value: "a",
            label:
              "a) Apenas anual ou semestral, com fechamento que leva semanas",
          },
          {
            value: "b",
            label:
              "b) Trimestralmente, mas com processo manual demorado e sujeito a revisões constantes",
          },
          {
            value: "c",
            label:
              "c) Mensalmente, porém com atraso de 30-45 dias e necessidade de ajustes posteriores frequentes",
          },
          {
            value: "d",
            label:
              "d) Mensalmente com fechamento em 15-30 dias e informações relativamente estáveis após fechamento",
          },
          {
            value: "e",
            label:
              "e) Mensalmente com fechamento em até 15 dias, processos bem estabelecidos de validação e conciliação, e capacidade de análises gerenciais em tempo quase real",
          },
        ],
      },
      {
        id: "q5_3",
        questionNumber: "5.3",
        text: "Como você avalia atualmente a rentabilidade dos seus clientes?",
        options: [
          {
            value: "a",
            label:
              "a) Não temos visibilidade de rentabilidade por cliente - avaliamos apenas margem bruta geral ou por família de produtos",
          },
          {
            value: "b",
            label:
              "b) Avaliamos clientes apenas por volume de compras ou valor médio de compra, sem considerar custos de servir e condições comerciais",
          },
          {
            value: "c",
            label:
              "c) Temos análises pontuais ou estimativas de rentabilidade para alguns clientes grandes, mas sem metodologia consistente",
          },
          {
            value: "d",
            label:
              "d) Temos análise de rentabilidade por cliente considerando margem de produtos vendidos, mas custos de distribuição e comerciais não são alocados de forma justa",
          },
          {
            value: "e",
            label:
              "e) Temos metodologia estruturada de rentabilidade por cliente considerando diversos custos, mas reconhecemos oportunidades de melhoria na precisão das alocações e critérios de custeio",
          },
        ],
      },
      {
        id: "q5_4",
        questionNumber: "5.4",
        text: "Quais decisões estratégicas sua empresa gostaria de tomar com base em análises de rentabilidade mais precisas?",
        options: [
          {
            value: "a",
            label:
              "a) No momento não vemos necessidade clara - nosso foco está em crescer volume de vendas e conquistar novos clientes",
          },
          {
            value: "b",
            label:
              "b) Gostaríamos de entender melhor quais clientes e produtos são mais rentáveis, mas sem planos específicos de ação",
          },
          {
            value: "c",
            label:
              "c) Queremos identificar oportunidades de renegociação de condições comerciais e prazos com clientes menos rentáveis",
          },
          {
            value: "d",
            label:
              "d) Buscamos otimizar nosso portfólio (produtos, clientes, fornecedores) e melhorar eficiência logística nas rotas menos rentáveis",
          },
          {
            value: "e",
            label:
              "e) Queremos transformar nossa operação com decisões baseadas em rentabilidade real: pricing por cliente/produto, otimização de mix, redesenho de rotas, renegociação com fornecedores, frequência de entrega e descontinuação de relações comerciais não rentáveis",
          },
        ],
      },
      {
        id: "q5_5",
        questionNumber: "5.5",
        text: "Qual o nível de maturidade analítica da sua organização?",
        options: [
          {
            value: "a",
            label:
              "a) Decisões baseadas principalmente em experiência e intuição - análises quantitativas são raras",
          },
          {
            value: "b",
            label:
              "b) Utilizamos relatórios básicos de vendas e indicadores operacionais, mas análises mais profundas são ocasionais",
          },
          {
            value: "c",
            label:
              "c) Temos equipe dedicada a análise de dados gerando descobertas, mas adoção limitada nas decisões de negócio",
          },
          {
            value: "d",
            label:
              "d) Área de análise de dados está integrada ao processo decisório com painéis gerenciais e análises regulares, mas há lacunas significativas em dados de custos e rentabilidade",
          },
          {
            value: "e",
            label:
              "e) Cultura de decisões baseadas em dados bem estabelecida com forte patrocínio da liderança e times preparados para consumir e agir sobre descobertas analíticas complexas",
          },
        ],
      },
    ],
  },
];

// Função para gerar código único no frontend
const generateCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
    if (i === 3) code += "-";
  }
  return code;
};

export default function AssessmentDistribuidores() {
  const classes = useStyles();

  // State variables
  const [sessionCode, setSessionCode] = useState("");
  const [inputCode, setInputCode] = useState("");
  const [distributorName, setDistributorName] = useState("");
  const [respondentName, setRespondentName] = useState("");
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [copied, setCopied] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Refs for debouncing auto-save
  const saveTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);
  const isLoadingSession = useRef(false);
  const isSavingRef = useRef(false); // Track if a save is in progress

  // Load session from backend
  const loadSession = useCallback(async (code) => {
    setIsLoading(true);
    setLoadError("");
    isLoadingSession.current = true;

    try {
      const response = await client.post("api/assessment/load_session/", {
        access_code: code,
      });

      if (response.data) {
        setSessionCode(response.data.access_code);
        setDistributorName(response.data.distributor_name || "");
        setRespondentName(response.data.respondent_name || "");

        // Convert backend format to frontend format
        const loadedAnswers = {};
        Object.keys(response.data.answers || {}).forEach((qId) => {
          const answer = response.data.answers[qId];
          loadedAnswers[qId] = {
            value: answer.value,
            label: answer.label,
          };
        });

        setAnswers(loadedAnswers);
        setComments(response.data.comments || {});
        setInputCode("");
        setLoadError("");

        // Reset the loading flag after a brief delay to prevent immediate auto-save
        setTimeout(() => {
          isLoadingSession.current = false;
        }, 1000);
      }
    } catch (error) {
      console.error("Error loading session:", error);
      if (error.response?.status === 404) {
        setLoadError("Código não encontrado. Verifique e tente novamente.");
      } else {
        setLoadError("Erro ao carregar sessão. Tente novamente.");
      }
      isLoadingSession.current = false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-save to backend (debounced)
  const saveToBackend = useCallback(async () => {
    // Prevent simultaneous saves
    if (isSavingRef.current || !sessionCode || !distributorName.trim()) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      await client.post("api/assessment/save_responses/", {
        access_code: sessionCode,
        distributor_name: distributorName,
        respondent_name: respondentName,
        answers: answers,
        comments: comments,
      });
    } catch (error) {
      console.error("Error saving responses:", error);
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  }, [sessionCode, distributorName, respondentName, answers, comments]);

  // Initialize session on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get("code");

    if (codeFromUrl) {
      loadSession(codeFromUrl.toUpperCase());
    } else {
      // Generate code locally - session will be created on backend only when user saves
      const newCode = generateCode();
      setSessionCode(newCode);
    }
  }, [loadSession]);

  // Auto-save effect with debouncing
  useEffect(() => {
    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Skip if currently loading a session
    if (isLoadingSession.current) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save with longer delay (3 seconds)
    if (sessionCode && distributorName.trim()) {
      saveTimeoutRef.current = setTimeout(() => {
        saveToBackend();
      }, 3000); // Increased from 1500ms to 3000ms
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    distributorName,
    respondentName,
    answers,
    comments,
    sessionCode,
    saveToBackend,
  ]);

  const handleLoadCode = () => {
    if (inputCode.trim()) {
      loadSession(inputCode.trim().toUpperCase());
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(sessionCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleAnswerChange = (questionId, selectedOption) => {
    setAnswers({
      ...answers,
      [questionId]: selectedOption,
    });
  };

  const handleCommentChange = (questionId, value) => {
    setComments({
      ...comments,
      [questionId]: value,
    });
  };

  const handleSubmit = async () => {
    if (!distributorName.trim()) {
      alert("Por favor, preencha o nome do distribuidor.");
      return;
    }

    if (!sessionCode) {
      alert("Erro: Código de sessão não encontrado.");
      return;
    }

    // Cancel any pending auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save one last time before submitting (if not already saving)
    if (!isSavingRef.current) {
      await saveToBackend();
    }

    try {
      await client.post("api/assessment/submit/", {
        access_code: sessionCode,
      });

      alert(
        "Questionário enviado com sucesso! Obrigado pela sua participação.",
      );
    } catch (error) {
      console.error("Error submitting assessment:", error);
      alert("Erro ao enviar questionário. Tente novamente.");
    }
  };

  const selectStyles = {
    container: (provided) => ({
      ...provided,
      width: "100%",
    }),
    control: (provided, state) => ({
      ...provided,
      // Green border when there's a value selected, otherwise normal behavior
      borderColor: state.hasValue
        ? "#4caf50"
        : state.isFocused
          ? integrationColor[0]
          : grayColor[5],
      borderWidth: "1px",
      boxShadow: state.isFocused
        ? state.hasValue
          ? "0 0 0 1px #4caf50"
          : `0 0 0 1px ${integrationColor[0]}`
        : provided.boxShadow,
      "&:hover": {
        borderColor: state.hasValue ? "#4caf50" : integrationColor[0],
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? integrationColor[0]
        : state.isFocused
          ? `${integrationColor[0]}20`
          : whiteColor,
      color: state.isSelected ? whiteColor : blackColor,
      cursor: "pointer",
      "&:active": {
        backgroundColor: `${integrationColor[0]}40`,
        // color: whiteColor,
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <>
      {/* Floating Saving Indicator */}
      {isSaving && (
        <div className={classes.savingIndicator}>
          <CircularProgress size={16} style={{ color: grayColor[7] }} />
          <span>Salvando...</span>
        </div>
      )}

      <div className={classes.content}>
        <Typography variant="h4" className={classes.title}>
          Questionário de assessment para distribuidores
        </Typography>

        {/* Aviso sobre uso de dados */}
        <div className={classes.privacyNotice}>
          <InfoOutlinedIcon className={classes.privacyIcon} />
          <div className={classes.privacyText}>
            <Typography
              className={classes.privacyText}
              style={{ marginBottom: "8px" }}
            >
              <strong>Uso e confidencialidade das informações:</strong> as
              informações fornecidas neste questionário serão utilizadas
              exclusivamente para fins de dimensionamento do esforço de
              implementação da plataforma Profit InSights, bem como para a
              elaboração de uma proposta comercial alinhada às necessidades
              específicas da sua operação.
            </Typography>
            <Typography
              className={classes.privacyText}
              style={{ marginBottom: "8px" }}
            >
              Ainda que não sejam coletados dados pessoais, reconhecemos que as
              informações compartilhadas podem refletir aspectos estratégicos do
              seu negócio. Por isso:
            </Typography>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li style={{ marginBottom: "4px" }}>
                Todas as respostas serão tratadas com confidencialidade e acesso
                restrito à equipe responsável pela análise e proposta.
              </li>
              <li style={{ marginBottom: "4px" }}>
                As informações não serão compartilhadas com terceiros, salvo
                mediante autorização expressa ou por obrigação legal.
              </li>
              <li style={{ marginBottom: "4px" }}>
                Adotamos medidas de segurança da informação para proteger os
                dados contra acessos não autorizados ou uso indevido.
              </li>
            </ul>
            <Typography
              className={classes.privacyText}
              style={{ marginTop: "8px" }}
            >
              Caso tenha dúvidas sobre o tratamento das informações, entre em
              contato conosco pelo e-mail legal@integrationconsulting.com.
            </Typography>
          </div>
        </div>

        <div className={classes.privacyNotice}>
          <InfoOutlinedIcon className={classes.privacyIcon} />
          <Typography className={classes.privacyText}>
            <strong>Respostas do questionário:</strong> caso as alternativas de
            resposta não reflitam exatamente a realidade da sua operação,
            escolha a alternativa que melhor se aproxima; adicionar comentários
            é <strong>opcional</strong>.
          </Typography>
        </div>

        {/* Seção de Código */}
        <Card className={classes.codeSection}>
          <CardBody>
            <Typography
              variant="h6"
              style={{ marginBottom: "10px", color: integrationColor[0] }}
            >
              Seu Código de Acesso
            </Typography>
            <Typography className={classes.infoText}>
              Este é o seu código único para este questionário. Se desejar,{" "}
              <strong>copie e guarde este código</strong> para poder retornar e
              continuar respondendo mais tarde, ou para revisar suas respostas.
            </Typography>

            {sessionCode && (
              <div className={classes.codeDisplay}>
                <span>{sessionCode}</span>
                <Tooltip title={copied ? "Copiado!" : "Copiar código"}>
                  <IconButton
                    size="small"
                    onClick={handleCopyCode}
                    style={{ color: integrationColor[0] }}
                  >
                    {copied ? <CheckCircleIcon /> : <FileCopyIcon />}
                  </IconButton>
                </Tooltip>
              </div>
            )}

            <GridContainer
              spacing={2}
              alignItems="center"
              style={{ marginTop: "20px" }}
            >
              <GridItem xs={12} sm={8} md={6}>
                <TextField
                  label="Digite seu código aqui caso já possua um"
                  variant="outlined"
                  fullWidth
                  value={inputCode}
                  onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  error={!!loadError}
                  helperText={loadError}
                  className={classes.commentField}
                  disabled={isLoading}
                />
              </GridItem>
              <GridItem
                xs={12}
                sm={4}
                md={3}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Button
                  subtle
                  color="integration"
                  onClick={handleLoadCode}
                  disabled={!inputCode.trim() || isLoading}
                  style={{ height: "54px", marginTop: "19px" }}
                >
                  {isLoading ? "Carregando..." : "Carregar Respostas"}
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>

        {/* Campos de Identificação */}
        <Card className={classes.nameFields}>
          <CardBody>
            <Typography
              variant="h6"
              style={{ marginBottom: "15px", color: integrationColor[0] }}
            >
              Identificação
            </Typography>
            <GridContainer spacing={3}>
              <GridItem xs={12} md={6}>
                <TextField
                  label={
                    <span>
                      Nome do Distribuidor
                      <span className={classes.requiredLabel}>*</span>
                    </span>
                  }
                  variant="outlined"
                  fullWidth
                  value={distributorName}
                  onChange={(e) => setDistributorName(e.target.value)}
                  className={classes.commentField}
                />
              </GridItem>
              <GridItem xs={12} md={6}>
                <TextField
                  label="Nome do Respondente (opcional)"
                  variant="outlined"
                  fullWidth
                  value={respondentName}
                  onChange={(e) => setRespondentName(e.target.value)}
                  className={classes.commentField}
                />
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>

        {questionnaireData.map((block) => (
          <div key={block.blockId} style={{ marginBottom: "40px" }}>
            <Typography variant="h5" className={classes.blockTitle}>
              {block.blockTitle}
            </Typography>

            {block.questions.map((question) => (
              <Card key={question.id} className={classes.questionCard}>
                <CardBody>
                  <Typography className={classes.questionText}>
                    {question.questionNumber}. {question.text}
                  </Typography>

                  <ReactSelect
                    options={question.options}
                    value={answers[question.id] || null}
                    onChange={(option) =>
                      handleAnswerChange(question.id, option)
                    }
                    placeholder="Selecione uma alternativa..."
                    styles={selectStyles}
                    isClearable
                    blurInputOnSelect
                    openMenuOnFocus={false}
                    isSearchable={false}
                  />

                  <TextField
                    label="Comentários adicionais (opcional)"
                    multiline
                    minRows={2}
                    maxRows={10}
                    variant="outlined"
                    fullWidth
                    className={classes.commentField}
                    value={comments[question.id] || ""}
                    onChange={(e) =>
                      handleCommentChange(question.id, e.target.value)
                    }
                  />
                </CardBody>
              </Card>
            ))}
          </div>
        ))}

        <GridContainer justifyContent="center">
          <GridItem xs={12} sm={6} md={4}>
            <Button
              color="integration"
              size="lg"
              block
              className={classes.submitButton}
              onClick={handleSubmit}
            >
              Enviar Questionário
            </Button>
          </GridItem>
        </GridContainer>
      </div>
    </>
  );
}
